import * as fromTypes from "../actions/types";
import * as R from "ramda";
import moment from "moment";
import { updateFlags } from "utils";

export const defaultState = {
	organisationName: "",
	startDate: new moment(),
	nextBillingDate: new moment(),
	canceledAtDate: new moment(),
	cancelAtDate: new moment(),
	brand: "",
	last4: "",
	expiryYear: null,
	expiryMonth: null,
	isTrial: false,
	isAddPaymentModalShowing: false,
	status: "",
	nextBillingAmount: 0.0,
	baseSubscriptionAmount: 0.0,
	totalAddOnAmount: 0.0,
	addOns: [],
	creditCardToken: {},
	creditCardTokenErrorMessage: "",
	// Subscription lifecycle
	subscriptionStatus: "Active",
	gracePeriodEndDate: null,
	failureReason: null,
	recentTransactions: [],
	// Grace period warning popup (shown once per session)
	showWarningPopup: false,
	warningDaysRemaining: 0,
	flags: {
		fetch: "none",
		updateStatus: "none",
		extend: "none",
	},
};

const subcriptionFetchSuccess = (state, { payload }) => {
	if (payload) {
		state.organisationName = payload.organisationName;
		state.brand = payload.brand;
		state.last4 = payload.last4;
		state.expiryYear = payload.expiryYear;
		state.expiryMonth = payload.expiryMonth;
		state.startDate = payload.startDate;
		state.numberOfUsers = payload.numberOfUsers;
		state.isTrial = payload.status === "trialing";
		state.nextBillingDate = payload.nextBillingDate;
		state.canceledAtDate = payload.canceledAtDate;
		state.cancelAtDate = payload.cancelAtDate;
		state.status = payload.status;
		state.nextBillingAmount = payload.nextBillingAmount;
		state.baseSubscriptionAmount = payload.baseSubscriptionAmount || 0;
		state.totalAddOnAmount = payload.totalAddOnAmount || 0;
		state.addOns = payload.addOns || [];
		state.subscriptionStatus = payload.subscriptionStatus || "Active";
		state.gracePeriodEndDate = payload.gracePeriodEndDate || null;
		state.failureReason = payload.failureReason || null;
		state.recentTransactions = payload.recentTransactions || [];
	}

	return updateFlags(state, { fetch: "done" });
};

const addPaymentModalReset = (state) => {
	state.isAddPaymentModalShowing = true;
	state.creditCardTokenErrorMessage = "";

	return updateFlags(state, { updateStatus: "none" });
};

const subscriptionReducer = (state = defaultState, action) => {
	switch (action.type) {
		// fetch card details
		case fromTypes.SUBSCRIPTION_FETCH:
			return updateFlags(R.clone(state), { fetch: "processing" });
		case fromTypes.SUBSCRIPTION_FETCH_FAILED:
			return updateFlags(R.clone(state), { fetch: "error" });
		case fromTypes.SUBSCRIPTION_FETCH_SUCCESS:
			return subcriptionFetchSuccess(R.clone(state), action);

		// toggle add payment method modal
		case fromTypes.SUBSCRIPTION_ADD_PAYMENT:
			return addPaymentModalReset(R.clone(state));
		case fromTypes.SUBSCRIPTION_ADD_PAYMENT_CLOSE:
			return { ...state, isAddPaymentModalShowing: false };

		// add card
		case fromTypes.SUBSCRIPTION_ADD_PAYMENT_SAVE:
			return updateFlags(R.clone(state), { updateStatus: "processing" });
		case fromTypes.SUBSCRIPTION_ADD_PAYMENT_SAVE_FAILED:
			return subcriptionAddPaymentSaveFailed(R.clone(state), action);
		case fromTypes.SUBSCRIPTION_ADD_PAYMENT_SAVE_SUCCESS:
			return subcriptionAddPaymentSaveSuccess(R.clone(state), action);
		case fromTypes.SUBSCRIPTION_STRIPE_ERROR_RECEIVED:
			return stripeErrorReceived(R.clone(state), action);

		// update card
		case fromTypes.SUBSCRIPTION_UPDATE_PAYMENT_SAVE:
			return updateFlags(R.clone(state), { updateStatus: "processing" });
		case fromTypes.SUBSCRIPTION_UPDATE_PAYMENT_SAVE_SUCCESS:
			return subcriptionUpdatePaymentSaveSuccess(R.clone(state), action);
		case fromTypes.SUBSCRIPTION_UPDATE_PAYMENT_SAVE_FAIL:
			return subcriptionUpdatePaymentSaveFail(R.clone(state), action);

		case fromTypes.SUBSCRIPTION_MODAL_SUBMIT:
			return updateFlags(R.clone(state), { updateStatus: "processing" });

		// delete card
		case fromTypes.SUBSCRIPTION_CANCEL_SUBSCRIPTION:
			return updateFlags(R.clone(state), { delete: "processing" });
		case fromTypes.SUBSCRIPTION_CANCEL_SUBSCRIPTION_FAILED:
			return updateFlags(R.clone(state), { delete: "error" });
		case fromTypes.SUBSCRIPTION_CANCEL_SUBSCRIPTION_SUCCESS:
			return updateFlags(R.clone(state), { delete: "done" });

		// subscription warning popup (from login response)
		case fromTypes.SUBSCRIPTION_WARNING_SET: {
			const s = R.clone(state);
			s.showWarningPopup = true;
			s.warningDaysRemaining = action.payload.daysRemaining || 0;
			s.gracePeriodEndDate = action.payload.gracePeriodEnd || null;
			s.subscriptionStatus = action.payload.subscriptionStatus || state.subscriptionStatus;
			return s;
		}
		case fromTypes.SUBSCRIPTION_WARNING_DISMISS: {
			const s = R.clone(state);
			s.showWarningPopup = false;
			return s;
		}

		// super admin extend
		case fromTypes.SUBSCRIPTION_EXTEND_REQUEST:
			return updateFlags(R.clone(state), { extend: "processing" });
		case fromTypes.SUBSCRIPTION_EXTEND_SUCCESS:
			return updateFlags(R.clone(state), { extend: "done" });
		case fromTypes.SUBSCRIPTION_EXTEND_FAILED:
			return updateFlags(R.clone(state), { extend: "error" });

		default:
			return state;
	}
};

const subcriptionAddPaymentSaveFailed = (state, action) => {
	// payload may be string or { message }
	state.creditCardTokenErrorMessage =
		(action && action.payload && action.payload.message) ||
		action.payload ||
		"There was a problem updating your credit card. Please contact support for more information.";

	return updateFlags(state, { updateStatus: "error" });
};

const subcriptionAddPaymentSaveSuccess = (state, action) => {
	state.isAddPaymentModalShowing = false;
	state.creditCardTokenErrorMessage = "";
	return updateFlags(state, { updateStatus: "done", fetch: "done" });
};

const stripeErrorReceived = (state, action) => {
	state.creditCardTokenErrorMessage = action.payload;
	return state;
};

const subcriptionUpdatePaymentSaveFail = (state, action) => {
	state.creditCardTokenErrorMessage =
		(action && action.payload && action.payload.message) ||
		action.payload ||
		"There was a problem updating your credit card. Please contact support for more information.";
	return updateFlags(state, { updateStatus: "error" });
};

const subcriptionUpdatePaymentSaveSuccess = (state, action) => {
	state.creditCardTokenErrorMessage = "";
	return updateFlags(state, { updateStatus: "done" });
};

export default subscriptionReducer;
